import Navigation from "@/components/Navigation";
import HeroBanner from "@/components/HeroBanner";
import PricingCTA from "@/components/PricingCTA";
import FeaturedBillboards from "@/components/FeaturedBillboards";
import FeaturedPackages from "@/components/FeaturedPackages";
import MillionViewInventories from "@/components/MillionViewInventories";
import ConvenienceUSP from "@/components/ConvenienceUSP";
import CoverageUSP from "@/components/CoverageUSP";
import ROIUSP from "@/components/ROIUSP";
import ClientsServed from "@/components/ClientsServed";
import Testimonials from "@/components/Testimonials";
import FAQ from "@/components/FAQ";
import ContactForm from "@/components/ContactForm";
import Footer from "@/components/Footer1";
import { useEffect, useState } from "react";
import type { Configuration } from "@/types/config";
import { normalizeDefault } from "@/utils/validationUtils";
import SustainabilitySection from "@/components/SustainabilitySection";
import { useGeolocation } from '@/utils/simpleGeolocation';
import CountrySelectionModal from '@/components/CountrySelectionModal';
import { useQuery } from "@apollo/client";
import { GET_MARKETPLACE_COUNTRIES } from "@/Graphql/Queries";

interface LandingProps {
  yourLogo: string;
  footerLogo: string;
  currencyCode: string;
  countryId: string;
  countryName: string;
  base_path: string;
  exposure: string;
}

export default function Landing({ yourLogo, footerLogo, currencyCode, countryId, countryName, base_path, exposure }: LandingProps) {

  const [loadingConfig, setLoadingConfig] = useState(true);
  const [isLandingPage, setIsLandingPage] = useState(true);
  const [configData, setConfigData] = useState<any[]>([]);
  const [contentData, setContentData] = useState<any[]>([]);
  const [isLoginEnabled, setIsLoginEnabled] = useState(true);
  const [isExploreEnabled, setIsExploreEnabled] = useState(true);

  // Simple geolocation on page load
  const { result: geoResult } = useGeolocation();

  // Country list query for popup
  const { data: countryData, loading: countryLoading } = useQuery(GET_MARKETPLACE_COUNTRIES, {
    variables: { exposure: exposure }
  });

  // Country selection popup state
  const [showCountryPopup, setShowCountryPopup] = useState(false);
  const [selectedCountryFromPopup, setSelectedCountryFromPopup] = useState<any>(null);

  // Log geolocation result and handle country selection popup
  useEffect(() => {
    if (geoResult && countryData?.getMarketplaceCountries) {
      console.log('🌍 Landing - Geolocation result:', geoResult);
      
      // Check if user has already selected a country in this session
      const hasSessionDefault = localStorage.getItem('sessionDefaultCountryId');
      
      // If session default exists, skip everything
      if (hasSessionDefault) {
        console.log('✅ Session country already set, skipping popup');
        return;
      }
      
      // Check if geolocation succeeded
      if (geoResult.success && geoResult.country) {
        console.log(`✅ Landing - User location: ${geoResult.country} (IP: ${geoResult.ipAddress})`);
        
        // Check if detected country is available in our list
        const countryOptions = countryData.getMarketplaceCountries || [];
        const searchCountry = geoResult.country.toLowerCase().trim();
        
        // Try exact match first
        let availableCountry = countryOptions.find((c: any) => 
          c.name?.toLowerCase().trim() === searchCountry
        );
        
        // If no exact match, try partial match
        if (!availableCountry) {
          availableCountry = countryOptions.find((c: any) => {
            const countryName = c.name?.toLowerCase().trim() || '';
            return countryName.includes(searchCountry) || searchCountry.includes(countryName);
          });
        }
        
        if (availableCountry) {
          // ✅ Detected country IS in the list - Auto-select it
          console.log('✅ IP country is available:', availableCountry.name);
          console.log('🆔 Country ID:', availableCountry.id);
          
          // Auto-save both IDs
          localStorage.setItem('sessionDefaultCountryId', availableCountry.id);
          localStorage.setItem('selectedCountryId', availableCountry.id);
          localStorage.setItem('sessionDefaultCountryName', availableCountry.name);
          
          console.log('💾 Auto-selected country (IP detection):', availableCountry.name);
          setSelectedCountryFromPopup(availableCountry);
          
          // Mark that we've handled this automatically
          sessionStorage.setItem('countryPopupShown', 'true');
        } else {
          // ❌ Detected country is NOT in the list - Show manual selection modal
          console.log('❌ IP country not available in our service area:', geoResult.country);
          console.log('🔔 Available countries:', countryOptions.map((c: any) => c.name).join(', '));
          console.log('🔔 Showing country selection popup for manual selection...');
          setShowCountryPopup(true);
        }
      } else {
        // Geolocation failed - Show manual selection modal
        console.warn('⚠️ Landing - Geolocation failed:', geoResult.error || geoResult.message);
        console.log('🔔 Showing country selection popup due to geolocation failure...');
        setShowCountryPopup(true);
      }
    }
  }, [geoResult, countryData]);

  // Handle country selection from modal
  const handleCountrySelect = (country: any) => {
    console.log('🌍 User selected country from modal:', country.name);
    setSelectedCountryFromPopup(country);
    
    // ✅ CRITICAL FIX: Save both IDs to ensure explore page works correctly
    localStorage.setItem('sessionDefaultCountryId', country.id);
    localStorage.setItem('selectedCountryId', country.id); // Save to selectedCountryId for immediate explore page access
    localStorage.setItem('sessionDefaultCountryName', country.name);
    
    // ✅ Mark popup as shown in this session (not localStorage, so it shows after logout)
    sessionStorage.setItem('countryPopupShown', 'true');
    
    console.log('💾 Saved country to localStorage:', {
      sessionDefaultCountryId: country.id,
      selectedCountryId: country.id,
      countryName: country.name
    });
    setShowCountryPopup(false);
  };

  // Fetch config and content (adapt as needed)
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        setLoadingConfig(true);
        const url = `${import.meta.env.VITE_APP_FOLDER_PATH}/json/configJson.json?t=${new Date().getTime()}`;
        const configResponse = await fetch(url);
        if (!configResponse.ok) throw new Error("Config fetch failed");
        const configData = await configResponse.json();
        setConfigData(configData);


        // Example: set landing page flag and logo
        const landingPageConfig = configData.find((page: any) => page.name === "Landing Page");

        console.log("Landing Page Config:", landingPageConfig);

        const landingPageEnabled = landingPageConfig?.configurations?.find((c: any) => c.key === "do_you_need_landing_page")?.default ?? true;
        setIsLandingPage(landingPageEnabled);

        const exploreConfig = landingPageConfig.configurations.find(
          (config: Configuration) => config.key === "Explore"
        );
        const loginConfig = landingPageConfig.configurations.find(
          (config: Configuration) => config.key === "login"
        );
        if (exploreConfig) {

          setIsExploreEnabled(normalizeDefault(exploreConfig.default));
        }
        if (loginConfig) {
          setIsLoginEnabled(normalizeDefault(loginConfig.default));
        }
        if (landingPageConfig) {
          const config = landingPageConfig.configurations.find(
            (config: Configuration) => config.key === "do_you_need_landing_page"
          );

          // Set landing page flag based on configuration
          // If config is undefined or not found, default to keeping the landing page visible
          const landingPageEnabled = config ? normalizeDefault(config.default) : true;
          setIsLandingPage(landingPageEnabled);

          const exploreConfig = landingPageConfig.configurations.find(
            (config: Configuration) => config.key === "Explore"
          );
          const loginConfig = landingPageConfig.configurations.find(
            (config: Configuration) => config.key === "login"
          );
          if (exploreConfig) {

            setIsExploreEnabled(normalizeDefault(exploreConfig.default));
          }
          if (loginConfig) {
            setIsLoginEnabled(normalizeDefault(loginConfig.default));
          }
        }
      } catch (error) {
        setIsLandingPage(true);
      } finally {
        setLoadingConfig(false);
      }
    };
    fetchConfig();
  }, []);

  const isFieldEnabled = (key: string, config: any[]) => {
    const landingPageConfig = config?.find((page: { name: string }) => page.name === "Landing Page");
    const configdata = landingPageConfig?.configurations || [];
    const fieldConfig = configdata?.find((field: any) => field.key === key);
    return fieldConfig ? normalizeDefault(fieldConfig.default) : true;
  };

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const url = `${import.meta.env.VITE_APP_FOLDER_PATH}/json/contentJson.json?t=${new Date().getTime()}`;

        const response = await fetch(url);

        if (!response.ok) {
          const errorText = await response.text();
          console.error("Error response:", errorText);
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const contentType = response.headers.get("content-type");
        if (!contentType || !contentType.includes("application/json")) {
          const text = await response.text();
          console.error("Non-JSON response:", text);
          throw new Error("Response is not JSON");
        }

        const data = await response.json();
        setContentData(data);

        const pageData = data.find((page: any) => page.name === "Landing Page");
        if (!pageData) {
          console.warn("Landing Page data not found");
          return;
        }
      } catch (error) {
        console.error("Error fetching content:", error);
      }
    };

    fetchContent();
  }, []);


  return (
    <div className="min-h-screen bg-warm-white">
      <Navigation
        configData={configData}
        yourLogo={yourLogo}
        base_path={base_path}
        mode="landing"
      />
      <HeroBanner
        isExploreEnabled={isExploreEnabled}
        isLoginEnabled={isLoginEnabled}
        contentData={contentData}
        configData={configData}
        base_path={base_path}
      />
      
      {/* Country Selection Modal */}
      <CountrySelectionModal
        open={showCountryPopup}
        onOpenChange={setShowCountryPopup}
        onCountrySelect={handleCountrySelect}
        detectedCountry={geoResult?.country}
        userIP={geoResult?.ipAddress}
        hasInventory={false}
        allowClose={true}
        exposure={exposure}
      />
      {isFieldEnabled('low_emission_high_impact', configData) && (
        <SustainabilitySection base_path={base_path}/>
      )}
      {isFieldEnabled('revolutionary pricing model', configData) && (
        <PricingCTA />
      )}
      {isFieldEnabled('featured packages', configData) && (
        <FeaturedPackages
          base_path={base_path}
          countryId={countryId}
          currencyCode={currencyCode}
          isExploreEnabled={isExploreEnabled}
          isLoginEnabled={isLoginEnabled}
          exposure={exposure}
        />
      )}
      {isFieldEnabled('featured properties', configData) && (
        <FeaturedBillboards
          base_path={base_path}
          countryId={countryId}
          currencyCode={currencyCode}
          isExploreEnabled={isExploreEnabled}
          isLoginEnabled={isLoginEnabled}
          exposure={exposure}
        />
      )}
      {isFieldEnabled('popular properties', configData) && (
        <MillionViewInventories

          countryId={countryId}
          base_path={base_path}
          isExploreEnabled={isExploreEnabled}
          isLoginEnabled={isLoginEnabled}
          exposure={exposure}
        />
      )}
      {isFieldEnabled('speed&efficiency', configData) && (
        <ConvenienceUSP
          isExploreEnabled={isExploreEnabled}
          isLoginEnabled={isLoginEnabled}
          base_path={base_path}
        />
      )}
      {isFieldEnabled('global coverage', configData) && (
        <CoverageUSP
          isExploreEnabled={isExploreEnabled}
          isLoginEnabled={isLoginEnabled}
          base_path={base_path}
        />
      )}
      {isFieldEnabled('ROI optimization', configData) && (
        <ROIUSP
          isExploreEnabled={isExploreEnabled}
          isLoginEnabled={isLoginEnabled}
          base_path={base_path}
        />
      )}
      {isFieldEnabled('clients we have served', configData) && (
        <ClientsServed />
      )}
      {isFieldEnabled('clients testimonials', configData) && (
        <Testimonials />
      )}
      {isFieldEnabled('faq', configData) && (
        <FAQ />
      )}
      {isFieldEnabled('contact form', configData) && (
        <ContactForm countryName={countryName}
          contentData={contentData}
          configData={configData}
        />
      )}
      <Footer configData={configData} yourLogo={footerLogo} base_path={base_path} />
    </div>
  );
}
